import { t, Trans } from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

import { PrivacySettingsColors } from './colors';
import PasskeyCopy from './PasskeyCopy';
import { CurrentPasskeySectionProps } from './types';

// ===== STYLED COMPONENTS =====

const CurrentPasskeyAlert = styled.div`
  background-color: ${PrivacySettingsColors.InfoBackground};
  border-radius: 4px;
  padding: 15px;
  font-size: 14px;
  margin-top: 10px;
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${PrivacySettingsColors.InfoText};

  .bff-private {
    margin-right: 8px;
    font-size: 18px;
  }
`;

const PasskeyDisplay = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  color: ${PrivacySettingsColors.VeryDarkText};

  > span {
    margin-right: 8px;
  }
`;

const PasskeyControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const PasskeyInput = styled.input`
  background: transparent;
  border: none;
  font-size: 13px;
  width: 300px;
  height: 30px;
  outline: none;

  &:disabled {
    background-color: ${PrivacySettingsColors.LightGrayBackground};
    color: ${PrivacySettingsColors.MutedText};
    cursor: not-allowed;
    opacity: 0.65;
  }

  &:hover {
      border: none;
      box-shadow: none;
      outline: none;
    }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  color: ${PrivacySettingsColors.DarkText};

  &:hover {
    opacity: 0.7;
  }
`;

const ExpirationInfo = styled.div`
  font-size: 13px;
  color: ${PrivacySettingsColors.VeryDarkText};
  margin-bottom: 8px;
`;

const ChangePasskeyButton = styled.button`
  background: none;
  border: none;
  color: ${PrivacySettingsColors.PurplePopsicle};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  padding: 5px 0 0 0;

  &:hover {
    text-decoration: none;
  }
`;

// ===== MAIN COMPONENT =====

/**
 * CurrentPasskeySection Component
 * Displays current passkey information with controls
 * Shows active passkey with copy, visibility toggle, and change options
 */
const CurrentPasskeySection: React.FunctionComponent<CurrentPasskeySectionProps> = ({
  libraryName,
  currentPasskey,
  displayExpirationDate,
  showCurrentPasskey,
  showNewPasskeyField,
  onToggleVisibility,
  onChangePasskey
}) => {
  /**
   * Formats passkey for display (visible or hidden with dots)
   */
  const formatPasskey = (passkey: string): string => {
    return showCurrentPasskey ? passkey : '•'.repeat(passkey.length);
  };

  return (
    <CurrentPasskeyAlert>
      <AlertHeader>
        <span className="bff-private" />
        <Trans>A passkey is currently set on this Private {libraryName}.</Trans>
      </AlertHeader>

      <PasskeyDisplay>
        <span><Trans>View Current Passkey:</Trans></span>
        <PasskeyControls>
          <PasskeyInput
            disabled
            readOnly
            type="text"
            value={formatPasskey(currentPasskey)}
          />
          <IconButton
            onClick={onToggleVisibility}
            title={showCurrentPasskey ? t`Hide passkey` : t`Show passkey`}
            type="button"
          >
            <span className={showCurrentPasskey ? 'bff-hide' : 'bff-show'} />
          </IconButton>
          <PasskeyCopy passkey={currentPasskey} />
        </PasskeyControls>
      </PasskeyDisplay>

      <ExpirationInfo>
        <Trans>Expiration Date:</Trans> <em>{displayExpirationDate || t`No expiration date set`}</em>
      </ExpirationInfo>

      {!showNewPasskeyField && (
        <ChangePasskeyButton
          onClick={onChangePasskey}
          type="button"
        >
          <Trans>Change Passkey</Trans>
        </ChangePasskeyButton>
      )}
    </CurrentPasskeyAlert>
  );
};

export default CurrentPasskeySection;
